import { useParams } from 'react-router-dom';
import { Paper, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  Estate_FieldsFragment,
  Estate_GetEstateByIdDocument,
  EstateStatus,
  useDocument_GetDocumentQuery,
  useEstate_DeleteEstateDocumentMutation,
} from 'queries';

import CloseX from 'common/close-x';
import Spinner from 'common/spinner';
import DocumentTypeImage from 'pages/vault-page/components/document-type-image';
import { allDocumentsUploaded, isEstateEditable } from 'utils/estate';
import { NotificationSeverity, showAlert } from 'utils/notification-manager';

type Props = {
  documentId: string;
  estateDocumentField: string;
  estate: Estate_FieldsFragment;
};

const useStyles = makeStyles(() => ({
  imageContainer: {
    display: 'inline-block',
    verticalAlign: 'middle',
  },
  paper: {
    padding: '10px',
    cursor: 'pointer',
    verticalAlign: 'middle',
    marginBottom: '15px',
    display: 'flex',
    alignItems: 'center',
  },
  title: {
    display: 'inline-block',
    marginLeft: '15px',
    fontSize: '.8rem',
  },
}));

export default function EstateDocumentPaper({ documentId, estateDocumentField, estate }: Props) {
  const classes = useStyles();
  const { portalId } = useParams<Record<string, string>>();

  const { data: documentData, loading: documentLoading } = useDocument_GetDocumentQuery({
    variables: {
      documentId: documentId || '',
      portalId: portalId,
    },
    onError: (e) => {
      showAlert({
        message: 'Error getting document: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const [deleteDocument] = useEstate_DeleteEstateDocumentMutation({
    refetchQueries: [
      {
        query: Estate_GetEstateByIdDocument,
        variables: { id: estate.id },
      },
    ],
    onError: (e) => {
      showAlert({
        message: 'Error deleting estate document: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const deleteUploadedDocument = (documentId: string) => {
    const estateId = estate.id || '';
    const estateStatus = allDocumentsUploaded(estate, undefined, estateDocumentField)
      ? EstateStatus.Payment
      : EstateStatus.DocumentUpload;
    // delete the document
    deleteDocument({
      variables: {
        documentId,
        estateId,
        portalId,
        estateStatus,
      },
    });
  };

  if (documentLoading) {
    return <Spinner />;
  }

  const isEditable = estate.status && isEstateEditable(estate.status);

  return (
    <Paper className={classes.paper}>
      <div className={classes.imageContainer}>
        <DocumentTypeImage fileName={documentData?.data.name || ''} isFolder={false} small={true} />
      </div>
      <Typography className={classes.title}>{documentData?.data.name}</Typography>
      <Typography>
        {isEditable && (
          <CloseX
            onClick={() => {
              deleteUploadedDocument(documentId || '');
            }}
            renderInline={true}
          />
        )}
      </Typography>
    </Paper>
  );
}
